
import { Box, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { usePatients } from '../api/index.js';
import { useCheckPermissions } from '../auth/index.js';
import ActivityReviewDrawer from '../components/Drawers/ActivityReviewDrawer.js';
import StreamsFilterDrawer from '../components/Drawers/StreamsFilterDrawer.js';
import { StreamContainer, Loading, MatchingPatientsList } from '../components/index.js';
import { LayoutGrid } from '../components/Layout/index.js';
import { useStreamSettings, useInactivityCheck } from '../hooks/index.js';
import { usePendingActivityEventsSync } from '../hooks/websocket-hooks/usePendingActivityEventsSync.js';
import { type Patient } from '../models/index.js';
import { useCurrentChannel } from '../providers/BroadcastChannel.js';
import { toggleActivityReviewDrawer, toggleFilterDrawer } from '../redux/reducers/drawers.js';
import {
  isActivityReviewDrawerOpen,
  isFilterDrawerOpen,
  isLayoutEditing,
} from '../redux/selectors.js';
import { Actions, sendMessage } from '../utils/index.js';

const STREAMS_LAYOUT_SCOPE = 'streams';

const Streams = () => {
  const filterDrawerOpen = useSelector(isFilterDrawerOpen);
  const reviewDrawerOpen = useSelector(isActivityReviewDrawerOpen);
  const editMode = useSelector(useMemo(() => isLayoutEditing(STREAMS_LAYOUT_SCOPE), []));
  const dispatch = useDispatch();
  const inactivity = useInactivityCheck();
  const { currentChannel } = useCurrentChannel();
  const { canReadPatient, canReadActivityEvent } = useCheckPermissions();
  const matchedPatients = usePatients({
    includeCameras: true,
    includeInactive: false,
    includeMatched: true,
  });
  const { isLoading, checkedItems, patients, onApplyStreamSettings, onToggle, onClear } = useStreamSettings(matchedPatients.refetch);

  const matchedPatientsAvailable = matchedPatients?.data && matchedPatients.data.length > 0;
  const visiblePatientIds = useMemo(() => (matchedPatients.data ?? []).map((p) => p.id), [matchedPatients.data]);
  usePendingActivityEventsSync(visiblePatientIds, canReadActivityEvent);

  const layoutItems = useMemo(
    () => (matchedPatients.data ?? []).map((p) => ({ id: p.id })),
    [matchedPatients.data],
  );

  const openPatientProfile = (patientId: string) => {
    if (currentChannel && canReadPatient) {
      sendMessage(currentChannel, {
        action: Actions.OPEN_PROFILE,
        data: {
          patientId,
        },
      });
    }
  };

  const onFilterDrawerClose = () => {
    onApplyStreamSettings();
    dispatch(toggleFilterDrawer(false));
  };

  const disabledButton = useMemo(() => {
    return !Object.values(checkedItems).includes(true);
  }, [checkedItems]);

  return (
    <div>
      {!matchedPatientsAvailable ? (
        isLoading || matchedPatients.isLoading ? (
          <Loading />
        ) : (
          <Box sx={{p: 2}}>
            {patients?.length ? <>
              <Typography>Please select a patient/s to begin streaming.</Typography>
              <MatchingPatientsList
                patients={patients}
                onToggle={onToggle}
                checkedItems={checkedItems}
                onApply={onApplyStreamSettings}
                disabledButton={disabledButton}
              />
            </>
              : <Typography>No Patients available!</Typography>}

          </Box>
        )
      ) : (
        <LayoutGrid
          scope={STREAMS_LAYOUT_SCOPE}
          editMode={editMode}
          items={layoutItems}
        >
          {(matchedPatients.data ?? []).map((patient: Patient, i: number) => (
            <div key={patient.id} data-test-id={`streams__item--${i}`}>
              <StreamContainer patient={patient} onClick={openPatientProfile} inactivity={inactivity} />
            </div>
          ))}
        </LayoutGrid>
      )}

      <StreamsFilterDrawer
        onClear={async () => {
          await onClear();
          await matchedPatients.refetch();
          dispatch(toggleFilterDrawer(false));
        }}
        open={filterDrawerOpen}
        onClose={onFilterDrawerClose}
        checkedItems={checkedItems}
        patients={patients}
        onToggle={onToggle}
        disabledButton={disabledButton}
      />
      {canReadActivityEvent ? (
        <ActivityReviewDrawer
          open={reviewDrawerOpen}
          onClose={() => dispatch(toggleActivityReviewDrawer(false))}
          visiblePatients={matchedPatients.data ?? []}
        />
      ) : null}
    </div>
  );
};

export default Streams;
